import {
  DeadEndCandidatesInputType,
  ValidSystemsDeadEndType
} from 'types/dead-end';

// Which configurations' diagnoses can explain the Choose Systems list (spec
// 001 T004): the complete configurations the page fetches, minus the fetched
// ones the customer has deselected in the configuration filter. A
// configuration with no combinations entry — its request degraded or failed
// — cannot be deselected (the filter only lists fetched configurations) and
// stays a candidate, so a scoring_unavailable diagnosis reaches the list
// beside the page's error banner instead of the generic filters note.
export function deadEndCandidateConfigIds({
  completeConfigIds,
  fetchedConfigs,
  selectedConfigIds
}: DeadEndCandidatesInputType): string[] {
  return completeConfigIds.filter(
    (id) =>
      !Object.prototype.hasOwnProperty.call(fetchedConfigs, id) ||
      selectedConfigIds.has(id)
  );
}

// A configuration dead-ended when its latest build stored a diagnosis AND
// left it with no systems to show. The second half matters: a
// scoring-degraded response stores its diagnosis but does not overwrite the
// combinations an earlier build produced, so the customer may still be
// looking at those — and if the client-side filters hide them all, the list
// is empty for the filters' reason, not the build's.
const isDeadEnded = (
  deadEndByConfig: Record<string, ValidSystemsDeadEndType>,
  fetchedConfigs: Record<string, ReadonlyArray<unknown>>,
  configId: string
): boolean =>
  deadEndByConfig[configId] !== undefined &&
  (fetchedConfigs[configId]?.length ?? 0) === 0;

// The diagnosis that explains an empty system list spanning the candidate
// configurations. The list is empty for a build reason only when EVERY
// candidate dead-ended; if any of them has systems — fresh or left over —
// the emptiness is the client-side filters' doing and no diagnosis applies:
// the generic "No systems match your filters." stays right. With several
// dead ends the first candidate's is returned.
export function deadEndForConfigs(
  deadEndByConfig: Record<string, ValidSystemsDeadEndType>,
  fetchedConfigs: Record<string, ReadonlyArray<unknown>>,
  configIds: Iterable<string>
): ValidSystemsDeadEndType | undefined {
  const ids = [...configIds];
  if (ids.length === 0) return undefined;
  return ids.every((id) => isDeadEnded(deadEndByConfig, fetchedConfigs, id))
    ? deadEndByConfig[ids[0]]
    : undefined;
}
